/**
 * Client-side search utilities for the COE Dashboard.
 *
 * Recent searches are stored in localStorage per-agent for the
 * "recent searches" dropdown in the search bar.
 *
 * Queries are patient names, emails and phone numbers, and COE workstations are
 * shared, so every read/write takes the agent's id and keys storage by it.
 * There is deliberately no un-scoped read/write: an agent signing in on the
 * same browser must never see the previous agent's searches.
 *
 * localStorage has no expiry, so sign-out has to remove them. `useThriveAuth`'s
 * `clearCredentials` calls `clearAllRecentSearches()`, which every sign-out
 * path goes through (menu, session expiry, other navbars) and which needs no
 * agent id.
 */

const RECENT_SEARCHES_KEY_PREFIX = 'coe-dashboard:recent-searches'
// Before searches were agent-scoped they lived under this one shared key. Never
// read or written any more, but a workstation could still hold it, so every
// clear removes it too.
const LEGACY_UNSCOPED_KEY = RECENT_SEARCHES_KEY_PREFIX
const MAX_RECENT = 10

function storageKey(agentId: string): string {
  return `${RECENT_SEARCHES_KEY_PREFIX}:${agentId}`
}

export interface RecentSearch {
  query: string
  type: string
  timestamp: number
}

export function getRecentSearches(agentId: string): RecentSearch[] {
  if (typeof window === 'undefined' || !agentId) {
    return []
  }

  try {
    const stored = localStorage.getItem(storageKey(agentId))

    if (!stored) {
      return []
    }

    const parsed: unknown = JSON.parse(stored)

    if (!Array.isArray(parsed)) {
      return []
    }

    return parsed.filter(isRecentSearch)
  } catch {
    return []
  }
}

function isRecentSearch(value: unknown): value is RecentSearch {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as RecentSearch).query === 'string' &&
    typeof (value as RecentSearch).type === 'string' &&
    typeof (value as RecentSearch).timestamp === 'number'
  )
}

export function addRecentSearch(
  agentId: string,
  search: Omit<RecentSearch, 'timestamp'>
): void {
  if (typeof window === 'undefined' || !agentId) {
    return
  }

  try {
    const recent = getRecentSearches(agentId).filter(
      (s) => s.query !== search.query || s.type !== search.type
    )
    recent.unshift({ ...search, timestamp: Date.now() })
    localStorage.setItem(
      storageKey(agentId),
      JSON.stringify(recent.slice(0, MAX_RECENT))
    )
  } catch {
    // localStorage full or unavailable — ignore
  }
}

/** Removes one agent's searches, and the legacy shared key. */
export function clearRecentSearches(agentId: string): void {
  if (typeof window === 'undefined' || !agentId) {
    return
  }

  try {
    localStorage.removeItem(storageKey(agentId))
    localStorage.removeItem(LEGACY_UNSCOPED_KEY)
  } catch {
    // ignore
  }
}

/**
 * Removes every agent's recent searches plus the legacy shared key. For
 * sign-out, where the outgoing agent's id isn't to hand and nobody's history
 * should outlive the session on a shared workstation.
 *
 * Every key is attempted on its own: one that refuses to go must not leave the
 * rest of the history behind. Returns true only if nothing was left over, so a
 * caller that cares (sign-out currently doesn't surface it) can tell the
 * difference. Storage being unavailable at all counts as "nothing left".
 */
export function clearAllRecentSearches(): boolean {
  if (typeof window === 'undefined') {
    return true
  }

  let keys: string[]

  try {
    // Collect first: removing while indexing shifts the remaining keys.
    keys = []

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)

      if (key?.startsWith(RECENT_SEARCHES_KEY_PREFIX)) {
        keys.push(key)
      }
    }
  } catch {
    // Can't even enumerate: storage is unavailable, so nothing was ever stored.
    return true
  }

  let allRemoved = true

  keys.forEach((key) => {
    try {
      localStorage.removeItem(key)
    } catch {
      allRemoved = false
    }
  })

  return allRemoved
}
